// pages/Supplier/SupplierDetails.js
import React, { useEffect, useState } from "react";
import Image from "../../components/Image";
import AddButton from "../SuppliersList/AddButton";
import { useNavigate, useParams } from "react-router-dom";
import EmptySvg from "../../components/EmptySvg";
import Loader from "../../components/Loader";
import CurrentProducts from "../ProductsList/CurrentProducts";
import DeleteModel from "../../components/Products/DeleteModel";
import { ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { API_URL } from "../../utils/config";
const baseUrl = API_URL;
const getLangFromLocalStorage = () => {
  return localStorage.getItem("language") || "en";
};

/* -------------------------------------------------- *
 *  Empty-state component
 * -------------------------------------------------- */
const Empty = () => {
  const { t } = useTranslation("supplier");
  return (
    <div className="flex flex-col gap-[8px] w-full h-full justify-center items-center">
      <EmptySvg />
      <h4 className=" h-[29px] font-inter font-semibold text-2xl leading-[29px] text-[#292929]">
        {t("supplierDetails.empty.title")}
      </h4>
      <p className="w-[459px] h-[44px] font-inter font-light text-sm text-center text-[#464646]">
        {t("supplierDetails.empty.subtitle")}
      </p>
    </div>
  );
};

/* -------------------------------------------------- *
 *  SupplierDetails main component
 * -------------------------------------------------- */
const SupplierDetails = ({ supplier }) => {
  const { t, i18n } = useTranslation("supplier");
  const navigate = useNavigate();
  const { id } = useParams();
  const lang = i18n.language;
  const { isAuthenticated, userRole } = useAuth();
  /* ---------------- state ---------------- */
  const [filter, setFilter] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [isDel, setIsDel] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const normalizeMultiValue = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || "").trim()).filter(Boolean);
    }
    if (typeof value === "string") {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return value ? [String(value)] : [];
  };

  const renderOverflowValue = (value, maxVisibleItems = 2) => {
    const allItems = normalizeMultiValue(value);
    if (allItems.length === 0) return "-";
    if (allItems.length <= maxVisibleItems) return allItems.join(", ");

    const visibleItems = allItems.slice(0, maxVisibleItems);
    const hiddenItems = allItems.slice(maxVisibleItems);
    const hiddenTooltipText = hiddenItems.join(", ");

    return (
      <span className="inline-flex items-center gap-1 relative group">
        <span>{visibleItems.join(", ")}</span>
        <span className="text-[#5507CF] font-bold">+{hiddenItems.length}</span>
        <span className="absolute left-1/2 top-full mt-2 -translate-x-1/2 hidden group-hover:block whitespace-nowrap rounded bg-[#1f1f1f] px-2 py-1 text-[12px] leading-4 text-white z-20">
          {hiddenTooltipText}
        </span>
      </span>
    );
  };

  /* --------------- helpers --------------- */
  const openWebsite = () => {
    const url = supplier.website.includes("https://")
      ? supplier.website
      : `https://${supplier.website}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  /** Toggle the action dropdown for a product row */
  const toggleDropdown = (index) => {
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  /* --------------- fetch data --------------- */
  useEffect(() => {
    const fetchService = async () => {
      if (!isAuthenticated) {
        setError(new Error("No authentication token found. Please log in."));
        setLoading(false);
        navigate("/login");
        return;
      }

      try {
        //GET /api/v1/attnd/attnd-supplier/services/my-supplier/{supplier_id}/services
        const res = await fetch(`${baseUrl}/api/v1/attnd/marketplace/supplier/${id}/services`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),

          },
        });

        const data = await res.json();

        if (!res.ok) {
          if (res.status === 401) {
            navigate("/login");
            throw new Error("Unauthorized access. Please log in again.");
          }
          if (res.status === 404) {
            alert(t("supplierDetails.errors.deleted"));
          }
          throw new Error(`An error occurred: ${data.error || res.statusText}`);
        }
        setProducts(data.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    !isDeleting && fetchService();
  }, [baseUrl, id, isDeleting, navigate, t]);

  /* --------------- delete product --------------- */
  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      if (!isAuthenticated) return;

      const res = await fetch(`${baseUrl}/api/v1/attnd-supplier/services/${isDel}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),

        },
      });

      if (!res.ok) {
        if (res.status === 401) {
          navigate("/login");
          throw new Error("Unauthorized access. Please log in again.");
        }
        const er = await res.json();
        throw new Error(er.error);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setIsDeleting(false);
      setIsDel(false);
    }
  };

  /* --------------- derived ---------------- */
  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(filter.toLowerCase())
  );

  /* --------------- loading / error --------------- */
  if (loading) {
    return (
      <div className="flex justify-center pt-[24px]">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        {t("supplierDetails.errors.fetchVenues")}: {error.message}
      </div>
    );
  }

  /* --------------- UI --------------- */
  return (
    <div className="flex flex-col w-full">
      {/* ---------- Supplier hero ---------- */}
      <div className="flex flex-row box-border py-[48px] px-[24px] bg-white">
        <Image
          src={supplier.image}
          alt="supplier"
          Class="box-border w-[270.5px] h-[208px] border border-[#5507CF] rounded-lg"
        />
        <div
          className={`flex flex-col w-full ${lang === "ar" ? "mr-[20px]" : "ml-[20px]"
            }`}
        >
          <div className="flex flex-row justify-between">
            <h3 className="h-[32px] font-inter font-semibold text-[24px] leading-[32px] text-[#5507CF]">
              {supplier.name}
            </h3>
            <div className="mb-1 flex flex-wrap gap-2 justify-end">
              {(() => {
                const types = normalizeMultiValue(
                  supplier.serviceTypeNames && supplier.serviceTypeNames.length
                    ? supplier.serviceTypeNames
                    : supplier.serviceType
                );
                if (types.length === 0) {
                  return (
                    <span className="font-inter font-bold text-[14px] leading-[17px] text-[#6609F8] bg-[#F4F1FF] w-fit px-3 py-2 rounded-lg">
                      -
                    </span>
                  );
                }
                const maxVisible = 2;
                const visibleTypes = types.slice(0, maxVisible);
                const hiddenTypes = types.slice(maxVisible);
                return (
                  <>
                    {visibleTypes.map((type, index) => (
                      <span
                        key={type + index}
                        className="font-inter font-bold text-[14px] leading-[17px] text-[#6609F8] bg-[#F4F1FF] w-fit px-3 py-2 rounded-lg"
                      >
                        {type}
                      </span>
                    ))}
                    {hiddenTypes.length > 0 && (
                      <span className="relative group inline-flex">
                        <span className="inline-flex items-center font-inter font-bold text-[14px] leading-[17px] text-[#6609F8] bg-[#F4F1FF] w-fit px-3 py-2 rounded-lg cursor-pointer">
                          +{hiddenTypes.length}
                        </span>
                        <span className="absolute left-1/2 top-full mt-2 -translate-x-[100%] hidden group-hover:block whitespace-nowrap rounded bg-[#1f1f1f] px-2 py-1 text-[12px] leading-4 text-white z-20">
                          <ul className="flex flex-col gap-1 list-disc pl-4">
                            {hiddenTypes.map((type, idx) => (
                              <li key={type + idx}>{type}</li>
                            ))}
                          </ul>

                        </span>
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          <h5 className="mt-[8px] h-[19px] font-inter font-semibold text-[16px] leading-[19px] text-[#6609F8]">
            {renderOverflowValue(
              supplier.cityNames && supplier.cityNames.length
                ? supplier.cityNames
                : supplier.city
            )}
          </h5>
          <p className="mt-[8px] font-inter text-[16px] leading-[24px] text-[#464646] text-justify">
            {supplier.about}
          </p>

          {/* contact row */}
          <div className="supplier-contact flex h-[24px] mt-[16px]">
            {/* email */}
            <div className="flex flex-row gap-1 font-inter font-semibold text-[16px] leading-[24px] text-[#464646]">
              {/* mail icon */}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17 21.25H7C6.59 21.25 6.25 20.91 6.25 20.5C6.25 20.09 6.59 19.75 7 19.75H17C19.86 19.75 21.25 18.36 21.25 15.5V8.5C21.25 5.64 19.86 4.25 17 4.25H7C4.14 4.25 2.75 5.64 2.75 8.5C2.75 8.91 2.41 9.25 2 9.25C1.59 9.25 1.25 8.91 1.25 8.5C1.25 4.85 3.35 2.75 7 2.75H17C20.65 2.75 22.75 4.85 22.75 8.5V15.5C22.75 19.15 20.65 21.25 17 21.25Z"
                  fill="#888888"
                />
                <path
                  d="M11.9998 12.87C11.1598 12.87 10.3098 12.61 9.65978 12.08L6.52978 9.57997C6.20978 9.31997 6.14978 8.84997 6.40978 8.52997C6.66978 8.20997 7.13977 8.14997 7.45977 8.40997L10.5898 10.91C11.3498 11.52 12.6398 11.52 13.3998 10.91L16.5298 8.40997C16.8498 8.14997 17.3298 8.19997 17.5798 8.52997C17.8398 8.84997 17.7898 9.32997 17.4598 9.57997L14.3298 12.08C13.6898 12.61 12.8398 12.87 11.9998 12.87Z"
                  fill="#888888"
                />
                <path
                  d="M8 17.25H2C1.59 17.25 1.25 16.91 1.25 16.5C1.25 16.09 1.59 15.75 2 15.75H8C8.41 15.75 8.75 16.09 8.75 16.5C8.75 16.91 8.41 17.25 8 17.25Z"
                  fill="#888888"
                />
                <path
                  d="M5 13.25H2C1.59 13.25 1.25 12.91 1.25 12.5C1.25 12.09 1.59 11.75 2 11.75H5C5.41 11.75 5.75 12.09 5.75 12.5C5.75 12.91 5.41 13.25 5 13.25Z"
                  fill="#888888"
                />
              </svg>
              {supplier.email}
            </div>

            {/* website show if only not null*/}
            {supplier.website && supplier.website.toLowerCase() !== "null" && (
              <div
                onClick={openWebsite}
                className="website-section cursor-pointer left-[419px] flex flex-row gap-1 font-inter font-semibold text-[16px] leading-[24px] text-[#464646]"
              >
                {/* globe icon */}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 22.75C6.07 22.75 1.25 17.93 1.25 12C1.25 6.07 6.07 1.25 12 1.25C17.93 1.25 22.75 6.07 22.75 12C22.75 17.93 17.93 22.75 12 22.75ZM12 2.75C6.9 2.75 2.75 6.9 2.75 12C2.75 17.1 6.9 21.25 12 21.25C17.1 21.25 21.25 17.1 21.25 12C21.25 6.9 17.1 2.75 12 2.75Z"
                    fill="#888888"
                  />
                  <path
                    d="M9.00023 21.75H8.00023C7.59023 21.75 7.25023 21.41 7.25023 21C7.25023 20.59 7.57023 20.26 7.98023 20.25C6.41023 14.89 6.41023 9.11 7.98023 3.75C7.57023 3.74 7.25023 3.41 7.25023 3C7.25023 2.59 7.59023 2.25 8.00023 2.25H9.00023C9.24023 2.25 9.47023 2.37 9.61023 2.56C9.75023 2.76 9.79023 3.01 9.71023 3.24C7.83023 8.89 7.83023 15.11 9.71023 20.77C9.79023 21 9.75023 21.25 9.61023 21.45C9.47023 21.63 9.24023 21.75 9.00023 21.75Z"
                    fill="#888888"
                  />
                  <path
                    d="M14.9999 21.7501C14.9199 21.7501 14.8399 21.7401 14.7599 21.7101C14.3699 21.5801 14.1499 21.1501 14.2899 20.7601C16.1699 15.1101 16.1699 8.89006 14.2899 3.23006C14.1599 2.84006 14.3699 2.41006 14.7599 2.28006C15.1599 2.15006 15.5799 2.36006 15.7099 2.75006C17.6999 8.71006 17.6999 15.2701 15.7099 21.2201C15.6099 21.5501 15.3099 21.7501 14.9999 21.7501Z"
                    fill="#888888"
                  />
                  <path
                    d="M12 17.2C9.21 17.2 6.43 16.81 3.75 16.02C3.74 16.42 3.41 16.75 3 16.75C2.59 16.75 2.25 16.41 2.25 16V15C2.25 14.76 2.37 14.53 2.56 14.39C2.76 14.25 3.01 14.21 3.24 14.29C8.89 16.17 15.12 16.17 20.77 14.29C21 14.21 21.25 14.25 21.45 14.39C21.65 14.53 21.76 14.76 21.76 15V16C21.76 16.41 21.42 16.75 21.01 16.75C20.6 16.75 20.27 16.43 20.26 16.02C17.57 16.81 14.79 17.2 12 17.2Z"
                    fill="#888888"
                  />
                  <path
                    d="M20.9998 9.74999C20.9198 9.74999 20.8398 9.73999 20.7598 9.70999C15.1098 7.82999 8.87978 7.82999 3.22978 9.70999C2.82978 9.83999 2.40978 9.62999 2.27978 9.23999C2.15978 8.83999 2.36978 8.41999 2.75978 8.28999C8.71978 6.29999 15.2798 6.29999 21.2298 8.28999C21.6198 8.41999 21.8398 8.84999 21.6998 9.23999C21.6098 9.54999 21.3098 9.74999 20.9998 9.74999Z"
                    fill="#888888"
                  />
                </svg>
                {supplier.website}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---------- section header + search + add ---------- */}
      <div className="flex flex-row box-border py-[40px] px-[24px] justify-between">
        <h5 className="w-[229px] h-[32px] font-inter font-extrabold text-[20px] leading-[32px] text-[#292929]">
          {t("supplierDetails.section.services")}
        </h5>

        <div className="flex flex-row gap-[16px]">
          {/* search */}
          <div className="box-border flex flex-row items-center p-[5px_16px] gap-2 w-[305px] h-[48px] bg-white rounded-lg">
            {/* search icon */}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11 20.75C5.62 20.75 1.25 16.38 1.25 11C1.25 5.62 5.62 1.25 11 1.25C16.38 1.25 20.75 5.62 20.75 11C20.75 16.38 16.38 20.75 11 20.75ZM11 2.75C6.45 2.75 2.75 6.45 2.75 11C2.75 15.55 6.45 19.25 11 19.25C15.55 19.25 19.25 15.55 19.25 11C19.25 6.45 15.55 2.75 11 2.75Z"
                fill="#464646"
              />
              <path
                d="M20.1601 22.79C20.0801 22.79 20.0001 22.78 19.9301 22.77C19.4601 22.71 18.6101 22.39 18.1301 20.96C17.8801 20.21 17.9701 19.46 18.3801 18.89C18.7901 18.32 19.4801 18 20.2701 18C21.2901 18 22.0901 18.39 22.4501 19.08C22.8101 19.77 22.7101 20.65 22.1401 21.5C21.4301 22.57 20.6601 22.79 20.1601 22.79ZM19.5601 20.49C19.7301 21.01 19.9701 21.27 20.1301 21.29C20.2901 21.31 20.5901 21.12 20.9001 20.67C21.1901 20.24 21.2101 19.93 21.1401 19.79C21.0701 19.65 20.7901 19.5 20.2701 19.5C19.9601 19.5 19.7301 19.6 19.6001 19.77C19.4801 19.94 19.4601 20.2 19.5601 20.49Z"
                fill="#464646"
              />
            </svg>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              type="text"
              placeholder={t("supplierDetails.search.placeholder")}
              className="flex flex-row items-center p-[5px_8px] gap-2 w-full outline-0 font-inter text-[14px] leading-[20px]"
            />
          </div>
          {/* Add product button - only show if user is not viewer */}
          {userRole !== 'viewer' && (
            <AddButton
              go={() => navigate("/products/add-new-product")}
              text={t("supplierDetails.buttons.addProduct")}
            />
          )}
        </div>
      </div>

      {/* ---------- list / empty / loader ---------- */}
      <div className="px-6 pb-[48px]">
        {products.length === 0 ? (
          <Empty />
        ) : (
          <CurrentProducts
            currentProducts={filteredProducts}
            navigate={navigate}
            toggleDropdown={toggleDropdown}
            dropdownOpen={dropdownOpen}
            supplier
            setIsDel={setIsDel}
          />
        )}
      </div>

      {/* ---------- delete confirmation modal ---------- */}
      {isDel && (
        <DeleteModel
          confirmDelete={handleDelete}
          cancelDelete={() => setIsDel(false)}
          isDeleting={isDeleting}
        />
      )}

      <ToastContainer position="top-right" />
    </div>
  );
};

export default SupplierDetails;
